import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import type { User } from '../types'
import { loadSchoolEmployees } from '../lib/apiPhpAdapter'
import { formatEmployeeFullName } from '../utils/employeeDisplay'
import { sortByPersonName } from '../utils/personNameSort'

type EmployeeUserPickerProps = {
  value: User | null
  onChange: (user: User | null) => void
  disabled?: boolean
}

const VISIBLE_WITHOUT_SEARCH = 50
const MAX_VISIBLE = 120

function normalizeUser(u: User): User {
  return {
    ...u,
    id: String(u.id),
    email: u.email ?? '',
  }
}

/** Výběr zaměstnance — vyhledávání + viditelný seznam (funguje i v dialozích). */
export function EmployeeUserPicker({
  value,
  onChange,
  disabled = false,
}: EmployeeUserPickerProps) {
  const [options, setOptions] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    loadSchoolEmployees()
      .then((list) => {
        if (!cancelled) {
          const normalized = sortByPersonName((Array.isArray(list) ? list : []).map(normalizeUser))
          setOptions(normalized)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Chyba načítání'
          setLoadError(msg)
          setOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = options
    if (q) {
      list = options.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          String(u.personal_number || '').toLowerCase().includes(q),
      )
    } else {
      list = options.slice(0, VISIBLE_WITHOUT_SEARCH)
    }
    return list.slice(0, MAX_VISIBLE)
  }, [options, search])

  const selectedId = value?.id ? String(value.id) : ''

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          Načítám seznam zaměstnanců…
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label="Hledat zaměstnance"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Jméno, e-mail nebo os. číslo…"
        disabled={disabled || !!loadError}
        autoComplete="off"
      />

      {loadError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          Nepodařilo se načíst zaměstnance: {loadError}
        </Typography>
      )}

      {!loadError && options.length === 0 && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
          Seznam zaměstnanců je prázdný — kontaktujte správce IT.
        </Typography>
      )}

      {!loadError && options.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
            {search.trim()
              ? `${filtered.length} výsledků — klikněte na jméno`
              : `Seznam zaměstnanců školy — prvních ${Math.min(VISIBLE_WITHOUT_SEARCH, options.length)} (abecedně), pište pro vyhledání`}
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              maxHeight: 280,
              overflow: 'auto',
              bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
            }}
          >
            <List dense disablePadding>
              <ListItemButton
                selected={!selectedId}
                disabled={disabled}
                onClick={() => {
                  onChange(null)
                  setSearch('')
                }}
              >
                <ListItemText
                  primary="Podat za sebe (nevybírat)"
                  primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                />
              </ListItemButton>

              {filtered.length === 0 ? (
                <ListItemButton disabled>
                  <ListItemText
                    primary="Žádný zaměstnanec neodpovídá hledání"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
              ) : (
                filtered.map((u) => {
                  const secondary = [
                    u.personal_number ? `os. č. ${u.personal_number}` : null,
                    u.email || null,
                  ]
                    .filter(Boolean)
                    .join(' · ')

                  return (
                  <ListItemButton
                    key={u.school_employee_id ?? u.id}
                    selected={selectedId === u.id}
                    disabled={disabled}
                    onClick={() => {
                      onChange(u)
                      setSearch(formatEmployeeFullName(u))
                    }}
                  >
                    <ListItemText
                      primary={formatEmployeeFullName(u)}
                      secondary={secondary || undefined}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                  )
                })
              )}
            </List>
          </Paper>

          {value && (
            <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
              Vybráno: <strong>{formatEmployeeFullName(value)}</strong>
              {value.email ? ` (${value.email})` : ''}
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}
